import React, { useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { User } from '../../../../types/task';
import { Task } from 'types/project/tasks';
import { useProjectDefaultQuery, useProjectQuery } from 'service/project';
import { formatDate } from '../../../../utils/helpers';
import { WorkTypeCategory } from 'types/project/projectDefaults';
import { RiRefreshLine, RiAddLine } from 'react-icons/ri';
import { Trash2, MoreVertical, ArrowUpToLine, ArrowDownToLine, Check, Minus } from 'lucide-react';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';


// Dropdown Imports
import StatusDropdown, { workStatusUIMap } from '../Dropdowns/StatusDropdown';
import UserDropdown from '../Dropdowns/UserDropdown';
import { PriorityDropdown } from '../Dropdowns/index';
import { workTypeUIMap } from '../Dropdowns/TaskTypeDropdown';
import { ColumnDef, Row } from '@tanstack/react-table';
import { ResizableTable } from '../../../common/ResizableTable';
import { arrayMove } from '@dnd-kit/sortable';
import EmptyState from '../../../common/EmptyState';
import { Tooltip } from '../../../common/Tooltip';
import StoryPointsInput from '../StoryPointsInput';

interface Props {
  tasks: Task[];
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask: (summary: string) => void;
  assigneeOptions: User[];
  reporterOptions: User[];
  onAssigneeChange: (taskId: string, assignee: User | null) => void;
  onReporterChange: (taskId: string, reporter: User | null) => void;
  onStatusChange: (taskId: string, status: string) => void;
  onPriorityChange: (taskId: string, priority: Task['workPriority']) => void;
  projectId: string;
  projectKey: string;
  isPending: boolean;
  visibleColumns?: string[];
  isReadOnly?: boolean;
  onRemove?: (task: Task) => void;
  isRemoving?: boolean;
  removeIcon?: React.ReactNode;
  removeTitle?: string;
  totalItems?: number;
  onRefresh?: () => void;
  onReorderWorkItem?: (activeId: string, overId: string) => void;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  isRefreshing?: boolean;
  loadMoreRef?: React.RefObject<HTMLDivElement | null>;
  isScrollable?: boolean;
  onLoadMoreChildren?: (parentUuid: string) => void;
  loadingParents?: Set<string>;
  renderEmptyState?: () => React.ReactNode;
  isCompact?: boolean;
  filterTask?: (task: Task) => boolean;
  onMoveToTop?: (taskId: string) => void;
  onMoveToBottom?: (taskId: string) => void;
  isRemoveDestructive?: boolean;
  isFilterApplied?: boolean;
  cumulativeLoaded?: number;
  /** Turns on multi-select: a checkbox on each top-level row and a select-all in the header. */
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  /** Adds a "Pts" column — each row's story points. */
  showPoints?: boolean;
  /** Makes a story's points editable in the "Pts" column; null clears them. */
  onStoryPointsChange?: (taskId: string, points: number | null) => void;
  /** Joins a page-level drag context so rows can be dragged to other lists. */
  dndContainerId?: string;
  /**
   * Fills the trailing actions column with a caller's own row menu. `index` and
   * `count` place the row among its siblings; `isTopLevel` is false for child rows.
   */
  renderRowActions?: (
    task: Task,
    position: { index: number; count: number; isTopLevel: boolean },
  ) => React.ReactNode;
  /** Shown right after a row's title, e.g. its Epic chip. */
  renderTitleSuffix?: (task: Task) => React.ReactNode;
}

/** A work item's story points, or 0 when it has none (only stories are estimated). */
export const getWorkItemPoints = (task: Task): number => Number(task.storyPoints ?? 0);

const SelectCheckbox = ({
  checked,
  indeterminate = false,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}) => (
  <span className="relative inline-flex h-4 w-4 shrink-0">
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="peer h-4 w-4 cursor-pointer appearance-none rounded-[4px] border border-[#3A3F4F] bg-[#1A1D27] transition-colors hover:border-[#5B6175] checked:border-[#8B5CF6] checked:bg-[#8B5CF6] indeterminate:border-[#8B5CF6] indeterminate:bg-[#8B5CF6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]/40"
    />
    {(checked || indeterminate) && (
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
        {indeterminate ? <Minus size={10} strokeWidth={3} /> : <Check size={10} strokeWidth={3} />}
      </span>
    )}
  </span>
);



const EditableTitleCell = React.memo(({
  task,
  projectId,
  onUpdateTitle,
  disabled,
  fitContent = false,
}: {
  task: Task;
  projectId: string;
  onUpdateTitle: (title: string) => void;
  disabled?: boolean;
  /**
   * Sized to the title rather than the cell, so something can follow it. Auto width,
   * not w-full: w-full ignores the -mx-1 and cuts a fitted title short by its padding.
   */
  fitContent?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(task.title || task.workItemTitle || '');

  const finishEditing = () => {
    setIsEditing(false);
    if (value.trim() !== (task.title || task.workItemTitle)) {
      onUpdateTitle(value);
    }
  };

  if (isEditing) {
    return (
      <input
        maxLength={255}
        className="block w-full bg-transparent border-b border-blue-500 focus:outline-none text-neutral-light min-w-0"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onBlur={finishEditing}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            finishEditing();
          } else if (e.key === 'Escape') {
            setValue(task.title || task.workItemTitle || '');
            setIsEditing(false);
          }
        }}
      />
    );
  }

  return (
    <div
      className={`block ${fitContent ? '' : 'w-full'} truncate ${disabled ? '' : 'cursor-text hover:bg-white/5'} px-1 -mx-1 rounded transition-colors`}
      onClick={() => { if (!disabled) setIsEditing(true); }}
      title={task.title || task.workItemTitle}
    >
      {task.title || task.workItemTitle || 'Untitled Task'}
    </div>
  );
});

const MoreActionsMenu = ({
  onMoveToTop,
  onMoveToBottom,
  onDelete,
  showMoveToTop,
  showMoveToBottom,
  deleteLabel,
  isRemoving,
  deleteIcon,
  isDestructive = true,
}: {
  onMoveToTop?: () => void;
  onMoveToBottom?: () => void;
  onDelete?: () => void;
  showMoveToTop: boolean;
  showMoveToBottom: boolean;
  deleteLabel?: string;
  isRemoving?: boolean;
  deleteIcon?: React.ReactNode;
  isDestructive?: boolean;
}) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="p-1.5 hover:bg-white/10 text-neutral-medium hover:text-white rounded-md transition-colors">
        <MoreVertical className="w-4 h-4" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          className="z-[100] w-40 rounded-lg bg-primary-variant border border-primary-subtle shadow-xl focus:outline-none py-1 [--anchor-gap:4px]"
        >
          {showMoveToTop && onMoveToTop && (
            <Menu.Item>
              {({ active }: { active: boolean }) => (
                <button
                  onClick={onMoveToTop}
                  className={`${
                    active ? 'bg-primary-subtle text-white' : 'text-neutral-medium'
                  } group flex w-full items-center px-3 py-2 text-xs transition-colors`}
                >
                  <ArrowUpToLine className="mr-2 h-3.5 w-3.5" />
                  Move to Top
                </button>
              )}
            </Menu.Item>
          )}
          {showMoveToBottom && onMoveToBottom && (
            <Menu.Item>
              {({ active }: { active: boolean }) => (
                <button
                  onClick={onMoveToBottom}
                  className={`${
                    active ? 'bg-primary-subtle text-white' : 'text-neutral-medium'
                  } group flex w-full items-center px-3 py-2 text-xs transition-colors`}
                >
                  <ArrowDownToLine className="mr-2 h-3.5 w-3.5" />
                  Move to Bottom
                </button>
              )}
            </Menu.Item>
          )}
          {((showMoveToTop && onMoveToTop) || (showMoveToBottom && onMoveToBottom)) && onDelete && (
            <div className="my-1 border-t border-primary-subtle" />
          )}
          {onDelete && (
            <Menu.Item>
              {({ active }: { active: boolean }) => (
                <button
                  onClick={onDelete}
                  disabled={isRemoving}
                  className={`${
                    active
                      ? (isDestructive ? 'bg-red-500/10 text-red-500' : 'bg-primary-subtle text-white')
                      : 'text-neutral-medium'
                  } group flex w-full items-center px-3 py-2 text-xs transition-colors disabled:opacity-50`}
                >
                  {deleteIcon ? (
                    <span className="mr-2 h-3.5 w-3.5 flex items-center justify-center shrink-0">{deleteIcon}</span>
                  ) : (
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                  )}
                  {deleteLabel || 'Delete'}
                </button>
              )}
            </Menu.Item>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

const AddTaskTableLayer: React.FC<Props> = React.memo(({
  tasks,
  onUpdateTask,
  onCreateTask,
  assigneeOptions,
  reporterOptions,
  onAssigneeChange,
  onReporterChange,
  onStatusChange,
  onPriorityChange,
  projectId,
  projectKey,
  isPending,
  visibleColumns,
  isReadOnly = false,
  onRemove,
  isRemoving = false,
  removeIcon,
  removeTitle,
  totalItems = 0,
  onRefresh,
  onReorderWorkItem,
  isFetchingNextPage,
  hasNextPage,
  isRefreshing = false,
  loadMoreRef,
  isScrollable = true,
  onLoadMoreChildren,
  loadingParents,
  renderEmptyState,
  isCompact = false,
  filterTask,
  onMoveToTop,
  onMoveToBottom,
  isRemoveDestructive = true,
  isFilterApplied = false,
  cumulativeLoaded,
  selectedIds,
  onSelectionChange,
  showPoints = false,
  onStoryPointsChange,
  dndContainerId,
  renderRowActions,
  renderTitleSuffix,
}) => {
  const { buildTaskUrl, buildCreateTaskUrl } = usePmoNavigation();
  const { data: defaultData } = useProjectDefaultQuery(projectId);
  const { data: project } = useProjectQuery(projectId);

  const [localTasks, setLocalTasks] = useState<Task[]>(tasks);
  const prevTasksRef = React.useRef(tasks);

  if (tasks !== prevTasksRef.current) {
    prevTasksRef.current = tasks;
    setLocalTasks(tasks);
  }

  const handleReorder = React.useCallback((activeId: string, overId: string) => {
    // The mutation call is a side effect and must not live inside the
    // setLocalTasks updater — React (StrictMode, in dev) invokes state
    // updaters twice to catch exactly this kind of impurity, which was
    // firing the reorder request twice per drag.
    setLocalTasks((prev) => {
      const oldIndex = prev.findIndex((t) => t.workItemUuid === activeId);
      const newIndex = prev.findIndex((t) => t.workItemUuid === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        return arrayMove(prev, oldIndex, newIndex);
      }
      return prev;
    });
    if (onReorderWorkItem) {
      onReorderWorkItem(activeId, overId);
    }
  }, [onReorderWorkItem]);

  // Helper to resolve the icon based on the task's workTypeUuid
  const getTaskTypeUI = React.useCallback((workTypeUuid: string) => {
    if (!defaultData?.workTypeDTOList) return { icon: null, color: '' };

    const type = defaultData.workTypeDTOList.find(
      (t) => t.workTypeUuid === workTypeUuid,
    );

    if (!type) return { icon: null, color: '' };

    return (
      workTypeUIMap[type.workTypeCategory as WorkTypeCategory] || {
        icon: null,
        color: '',
      }
    );
  }, [defaultData]);

  // Multi-select covers top-level rows only; children move with their parent.
  const isSelectable = !!onSelectionChange;
  const selectableIds = React.useMemo(
    () =>
      localTasks
        .filter((t) => !(t as any).isLoadMore && !(t as any).isLoading)
        .map((t) => t.workItemUuid),
    [localTasks],
  );
  const selectedCount = selectedIds
    ? selectableIds.filter((id) => selectedIds.has(id)).length
    : 0;

  const toggleSelected = React.useCallback(
    (id: string) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange],
  );

  const toggleAll = React.useCallback(() => {
    if (!onSelectionChange) return;
    const allSelected =
      selectableIds.length > 0 && selectableIds.every((id) => selectedIds?.has(id));
    onSelectionChange(allSelected ? new Set() : new Set(selectableIds));
  }, [selectableIds, selectedIds, onSelectionChange]);

  // Define TanStack Table Columns
  const columns = React.useMemo<ColumnDef<Task>[]>(
    () => [
      {
        accessorKey: 'workItemKey',
        header: isSelectable
          ? () => (
              <div className="flex items-center gap-3">
                <SelectCheckbox
                  label="Select all work items"
                  checked={selectableIds.length > 0 && selectedCount === selectableIds.length}
                  indeterminate={selectedCount > 0 && selectedCount < selectableIds.length}
                  onChange={toggleAll}
                />
                Work ID
              </div>
            )
          : 'Work ID',
        size: isCompact ? 120 : 160,
        cell: ({ row }: { row: Row<Task> }) => {
          const task = row.original;
          if ((task as any).isLoadMore || (task as any).isLoading) return null;

          const { icon, color } = getTaskTypeUI(task.workTypeUuid);
          return (
            <div className="flex items-center gap-2 cursor-pointer font-mono text-neutral-light">
              {isSelectable && row.depth === 0 && (
                <SelectCheckbox
                  label={`Select ${task.workItemKey}`}
                  checked={!!selectedIds?.has(task.workItemUuid)}
                  onChange={() => toggleSelected(task.workItemUuid)}
                />
              )}
              <span className={`${color} text-lg flex-shrink-0`}>{icon}</span>
              <Link
                className={`hover:underline ${task.workItemKey === 'SAVING...' ? 'animate-pulse text-neutral-medium cursor-wait pointer-events-none' : ''}`}
                to={task.workItemKey === 'SAVING...' ? '#' : buildTaskUrl(projectKey, task.workItemKey)}
              >
                {task.workItemKey}
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        size: isCompact ? 220 : 400,
        cell: ({ row }: { row: Row<Task> }) => {
          const task = row.original;
          if ((task as any).isLoading) {
            return (
               <div className="flex items-center gap-2 w-full">
                 <div className="h-4 w-3/4 bg-white/5 animate-pulse rounded" />
               </div>
            );
          }
          if ((task as any).isLoadMore) {
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onLoadMoreChildren) {
                    onLoadMoreChildren((task as any).parentWorkItemUuid);
                  }
                }}
                className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-all duration-200 flex items-center gap-2 uppercase tracking-widest bg-blue-500/10 hover:bg-blue-500/20 px-2 py-1 rounded group"
              >
                Show more child items <RiAddLine size={14} className="group-hover:rotate-90 transition-transform" />
              </button>
            );
          }

          const suffix = renderTitleSuffix?.(task);
          const titleCell = (
            <EditableTitleCell
              task={task}
              projectId={projectId}
              onUpdateTitle={(title) => {
                onUpdateTask(task.workItemUuid, {
                  title,
                  workItemTitle: title,
                });
              }}
              disabled={isReadOnly}
              fitContent={!!suffix}
            />
          );

          return (
            <div className="flex items-center gap-2 text-neutral-light text-[13px] w-full min-w-0">
              {/* With a suffix the title shrinks to its text, so the suffix sits right after it. */}
              {suffix ? <div className="min-w-0">{titleCell}</div> : titleCell}
              {suffix}
            </div>
          );
        },
      },
      {
        accessorKey: 'workStatusUuid',
        header: 'Status',
        size: isCompact ? 120 : 140,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          const task = row.original;

          // Fast path: use the category & name embedded in the task response — always
          // available regardless of whether defaultData has finished loading.
          // Falls back to full StatusDropdown only when the task lacks these fields
          // (e.g. optimistically-added rows that haven't round-tripped yet).
          if (task.workStatusCategory && task.workStatusName) {
            const category = task.workStatusCategory as keyof typeof workStatusUIMap;
            const ui = workStatusUIMap[category] ?? workStatusUIMap['TODO'];

            if (isReadOnly) {
              return (
                <span
                  className={`px-2 py-1 rounded-full font-medium text-xs border flex items-center gap-1.5 w-fit ${ui.bg} ${ui.border} ${ui.text}`}
                >
                  {task.workStatusName}
                </span>
              );
            }

            return (
              <StatusDropdown
                workStatuses={defaultData?.workStatusDTOList ?? []}
                currentStatusUuid={task.workStatusUuid!}
                onStatusChange={(status) => {
                  onStatusChange(task.workItemUuid, status.workStatusUuid);
                }}
                size="sm"
                disabled={isReadOnly}
              />
            );
          }

          // Fallback for rows without embedded status data
          return (
            <StatusDropdown
              workStatuses={defaultData?.workStatusDTOList ?? []}
              currentStatusUuid={task.workStatusUuid!}
              onStatusChange={(status) => {
                onStatusChange(task.workItemUuid, status.workStatusUuid);
              }}
              size="sm"
              disabled={isReadOnly}
            />
          );
        },
      },
      {
        id: 'points',
        header: 'Pts',
        size: 70,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          const { storyPoints, workTypeCategory } = row.original;
          // Only stories hold points; other types say why on hover.
          if (workTypeCategory !== 'STORY') {
            return (
              // As wide as a story's points box, so the dash sits under its centre.
              <Tooltip message="Only stories can be estimated." className="w-12 text-center cursor-not-allowed">
                <span className="text-neutral-medium text-sm">–</span>
              </Tooltip>
            );
          }
          if (!isReadOnly && onStoryPointsChange) {
            return (
              <StoryPointsInput
                compact
                value={storyPoints ?? null}
                placeholder="–"
                onCommit={(points) => onStoryPointsChange(row.original.workItemUuid, points)}
              />
            );
          }
          if (storyPoints === null || storyPoints === undefined) {
            return <span className="text-neutral-medium text-sm">–</span>;
          }
          return (
            <span className="px-2 py-0.5 rounded-md bg-primary-subtle text-xs font-semibold text-neutral tabular-nums">
              {storyPoints}
            </span>
          );
        },
      },
      {
        accessorKey: 'workItemAssigneeAppUserUuid',
        header: 'Assignee',
        size: 80,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          return (
            <UserDropdown
              users={project?.appUserList ?? []}
              currentUserUuid={row.original.workItemAssigneeAppUserUuid!}
              onUserChange={(user) => {
                onAssigneeChange(row.original.workItemUuid, user as any);
              }}
              placeholder="Unassigned"
              showEmail={false}
              size="sm"
              showName={false}
              showChevron={false}
              disabled={isReadOnly}
            />
          );
        },
      },
      {
        accessorKey: 'workPriority',
        header: 'Priority',
        size: 140,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          return (
            <PriorityDropdown
              currentPriority={row.original.workPriority}
              onPriorityChange={(priority) => {
                const formattedPriority = priority.toUpperCase();
                onPriorityChange(
                  row.original.workItemUuid,
                  formattedPriority as any,
                );
              }}
              size="sm"
              disabled={isReadOnly}
            />
          );
        },
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        size: 120,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          return (
            <span className="text-neutral-medium text-sm">
              {formatDate(row.original.createdDate)}
            </span>
          );
        },
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        size: 120,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          const date = row.original.dueDate || row.original.workItemDueDate;
          return (
            <span className="text-neutral-medium text-sm">
              {date ? formatDate(date) : '-'}
            </span>
          );
        },
      },
      {
        accessorKey: 'workItemReporterAppUserUuid',
        header: 'Reporter',
        size: 80,
        cell: ({ row }: { row: Row<Task> }) => {
          if ((row.original as any).isLoadMore) return null;
          return (
            <UserDropdown
              users={project?.appUserList ?? []}
              currentUserUuid={row.original.workItemReporterAppUserUuid!}
              onUserChange={(user) => {
                onReporterChange(row.original.workItemUuid, user as any);
              }}
              placeholder="Unassigned"
              showEmail={false}
              size="sm"
              showName={false}
              allowUnassigned={false}
              showChevron={false}
              disabled={isReadOnly}
            />
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }: { row: Row<Task> }) => {
          const task = row.original;
          const isFirst = row.index === 0;
          const isLast = row.index === (row.getParentRow()?.subRows?.length ?? tasks.length) - 1;

          if ((task as any).isLoadMore || (task as any).isLoading) return null;

          if (renderRowActions) {
            const parent = row.getParentRow();
            return (
              <div className="flex items-center justify-center">
                {renderRowActions(task, {
                  index: row.index,
                  count: parent?.subRows?.length ?? localTasks.length,
                  isTopLevel: row.depth === 0,
                })}
              </div>
            );
          }

          const hasMoveActions = (onMoveToTop && !isFirst) || (onMoveToBottom && !isLast);

          if (!hasMoveActions && onRemove) {
            return (
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(task);
                  }}
                  disabled={isRemoving}
                  className={`p-1.5 rounded-md transition-colors disabled:opacity-50 ${
                    isRemoveDestructive 
                      ? 'hover:bg-red-500/10 text-neutral-medium hover:text-red-500' 
                      : 'hover:bg-white/10 text-neutral-medium hover:text-white'
                  }`}
                  title={removeTitle || 'Remove'}
                >
                  {removeIcon || <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            );
          }

          return (
            <div className="flex items-center justify-center">
              <MoreActionsMenu
                onMoveToTop={onMoveToTop ? () => onMoveToTop(task.workItemUuid) : undefined}
                onMoveToBottom={onMoveToBottom ? () => onMoveToBottom(task.workItemUuid) : undefined}
                onDelete={onRemove ? () => onRemove(task) : undefined}
                showMoveToTop={!isFirst}
                showMoveToBottom={!isLast}
                deleteLabel={removeTitle}
                deleteIcon={removeIcon}
                isDestructive={isRemoveDestructive}
                isRemoving={isRemoving}
              />
            </div>
          );
        },
      },
    ].filter(col => {
      if ((col as any).id === 'actions') return !!onRemove || !!renderRowActions;
      if ((col as any).id === 'points') return showPoints;
      return !visibleColumns || visibleColumns.includes((col as any).accessorKey);
    }) as ColumnDef<Task>[],
    [
      defaultData, 
      project, 
      projectId, 
      onPriorityChange, 
      visibleColumns, 
      onRemove, 
      buildTaskUrl, 
      projectKey, 
      isReadOnly, 
      onUpdateTask, 
      onStatusChange, 
      onAssigneeChange, 
      onReporterChange, 
      isRemoving, 
      removeTitle, 
      removeIcon,
      getTaskTypeUI,
      onLoadMoreChildren,
      isCompact,
      onMoveToTop,
      onMoveToBottom,
      isRemoveDestructive,
      tasks.length,
      isSelectable,
      selectedIds,
      selectableIds,
      selectedCount,
      toggleSelected,
      toggleAll,
      showPoints,
      onStoryPointsChange,
      renderRowActions,
      renderTitleSuffix,
      localTasks.length,
    ],
  );

  // The backend now returns childWorkItems as nested arrays inside parent items.
  // We pass them directly to getSubRows so the table renders them as expandable children.
  const getSubRows = React.useCallback(
    (row: Task) => {
      // 1. Get raw children
      let children = row.childWorkItems || [];
      
      // 2. Apply frontend filtering to children (e.g. Hide Done, Search)
      if (filterTask) {
        children = children.filter(filterTask);
      }

      // 3. Show loading skeleton if this parent is currently fetching children
      if (loadingParents?.has(row.workItemUuid)) {
        return [
          ...children,
          { 
            workItemUuid: `loading-${row.workItemUuid}`, 
            isLoading: true, 
            parentWorkItemUuid: row.workItemUuid 
          } as any
        ];
      }
      
      // 4. Inject "Load More" row if there are more children on the server
      const paginationTotal = Number(row.childTotalElements || 0);
      const unfilteredLoaded = Number((row as any)._unfilteredChildrenCount ?? row.childWorkItems?.length ?? 0);
      
      if (paginationTotal > unfilteredLoaded) {
        return [
          ...children, 
          { 
            workItemUuid: `load-more-${row.workItemUuid}`, 
            isLoadMore: true, 
            parentWorkItemUuid: row.workItemUuid,
            title: 'Load More' 
          } as any
        ];
      }
      return children.length ? children : undefined;
    },
    [loadingParents, filterTask],
  );

  const getRowCanExpand = React.useCallback((row: Row<Task>) => {
    if (isFilterApplied) {
      return false;
    }
    const task = row.original;
    
    // If we have loaded children but none matched the filter, hide the arrow
    if ((task as any)._unfilteredChildrenCount > 0 && task.childWorkItems && task.childWorkItems.length === 0) {
      return false;
    }
    
    // childTotalElements is now the reliable source for expansion arrows.
    const total = task.childTotalElements ?? (task as any).totalChildWorkItemsCount ?? (task as any).totalSubTasksCount ?? 0;
    return total > 0;
  }, [isFilterApplied]);

  const handleRowExpand = React.useCallback((row: Task) => {
    if ((!row.childWorkItems || row.childWorkItems.length === 0) && onLoadMoreChildren) {
      onLoadMoreChildren(row.workItemUuid);
    }
  }, [onLoadMoreChildren]);

  const tableData = React.useMemo(() => {
    // Force a new array reference when loadingParents changes so TanStack Table recalculates getSubRows
    return [...localTasks];
  }, [localTasks, loadingParents]);

  return (
    <div className="bg-primary flex flex-col min-h-0 w-full flex-1">
      <ResizableTable
        data={tableData}
        columns={columns}
        isLoading={isPending}
        className="border-none flex-auto min-h-0"
        getSubRows={getSubRows}
        getRowCanExpand={getRowCanExpand}
        onRowExpand={handleRowExpand}
        getRowId={(row: Task) => row.workItemUuid}
        onLoadMoreChildren={onLoadMoreChildren}
        onReorder={(!isReadOnly && onReorderWorkItem) ? handleReorder : undefined}
        getRowCanDrag={(row) => row.depth === 0}
        isScrollable={isScrollable}
        isFilterApplied={isFilterApplied}
        dndContainerId={dndContainerId}
        renderEmptyState={renderEmptyState || (() => (
          isFilterApplied ? (
            <EmptyState
              title="No matching work items found"
              description="Try changing or clearing filters"
              showAddIcon={false}
            />
          ) : (
            <EmptyState
              title="Your workspace is ready for action"
              description="Start by creating your first task. Organize, track, and collaborate with your team to bring your vision to life."
              buttonLabel="Create Your First Task"
              to={buildCreateTaskUrl(projectKey)}
            />
          )
        ))}
        bottomContent={
          loadMoreRef && hasNextPage ? (
            <div ref={loadMoreRef} className="flex justify-center items-center shrink-0 w-full py-2 bg-primary">
              {isFetchingNextPage ? (
                <ClipLoader color="#8B5CF6" size={24} />
              ) : (
                <div className="h-px w-full" />
              )}
            </div>
          ) : undefined
        }
      />
      
      {/* Pagination Summary Footer - Jira Style */}
      {totalItems > 0 && (
        <div className="flex items-center justify-end px-4 py-2 bg-primary hover:bg-[#2D3748]/20 transition-all gap-4 border-t border-primary-intemediate border-x border-b rounded-b-lg -mt-[1px]">
          <span className="text-xs text-neutral-medium font-medium tabular-nums">
            {cumulativeLoaded ?? tasks.length} of {totalItems}
          </span>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1 rounded-md hover:bg-white/5 text-neutral-medium hover:text-neutral-light transition-colors group"
              title="Refresh tasks"
            >
              <RiRefreshLine size={16} className={`${isRefreshing ? 'animate-spin text-white' : 'group-active:rotate-180 transition-transform duration-500'}`} />
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default AddTaskTableLayer;
